import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import classnames from 'classnames'
import { Spinner } from 'react-bootstrap'
import { Button } from '@abroad/components'

const SessionDatePicker = ({
  selectedDate,
  onDateSelect,
  allowMultipleSelection = false,
  minDate,
  maxDate,
  currentMonth,
  onMonthChange,
  availableDays = {},
  enableAllDates = false, // When true, enables all dates except weekends (for viewing selected recurring dates)
  restrictionDates = [], // Array of date strings with session restrictions (saffron highlighting)
  unavailableDates = [], // Array of date strings that are unavailable (for red highlighting)
  highlightedDates = [], // Additional dates to visually highlight (e.g. all selected recurring dates)
  isLoading = false,
}) => {
  const normalizeSelectedDates = (value) => {
    if (!value) {
      return []
    }

    if (allowMultipleSelection && Array.isArray(value)) {
      return value.map((d) => moment(d).format('YYYY-MM-DD'))
    }

    return [moment(value).format('YYYY-MM-DD')]
  }

  const [currentMonthState, setCurrentMonthState] = useState(() => {
    if (currentMonth) {
      return moment(currentMonth)
    }

    if (selectedDate) {
      return moment(selectedDate)
    }

    return moment()
  })
  const [selectedDates, setSelectedDates] = useState(
    normalizeSelectedDates(selectedDate),
  )

  useEffect(() => {
    if (!currentMonth) {
      return
    }

    const nextMonth = moment(currentMonth)
    if (!nextMonth.isValid()) {
      return
    }

    setCurrentMonthState((prev) => {
      if (prev && prev.isSame(nextMonth, 'month')) {
        return prev
      }

      return nextMonth
    })
  }, [currentMonth])

  useEffect(() => {
    setSelectedDates(normalizeSelectedDates(selectedDate))
  }, [selectedDate, allowMultipleSelection])

  const availableDaysMap = useMemo(() => {
    if (Array.isArray(availableDays)) {
      return availableDays.reduce((acc, day) => {
        if (day?.date) {
          acc[day.date] = day
        }
        return acc
      }, {})
    }

    return availableDays || {}
  }, [availableDays])

  const highlightedDateSet = useMemo(() => {
    if (!highlightedDates || highlightedDates.length === 0) {
      return new Set()
    }

    return new Set(highlightedDates.map((d) => moment(d).format('YYYY-MM-DD')))
  }, [highlightedDates])

  const startOfMonth = currentMonthState.clone().startOf('month')
  const endOfMonth = currentMonthState.clone().endOf('month')
  const startDate = startOfMonth.clone().startOf('week')
  const endDate = endOfMonth.clone().endOf('week')

  const days = []
  let day = startDate.clone()

  while (day.isSameOrBefore(endDate, 'day')) {
    days.push(day.clone())
    day.add(1, 'day')
  }

  const isSelected = (date) => {
    const dateStr = date.format('YYYY-MM-DD')
    return selectedDates.includes(dateStr)
  }

  const isDisabled = (date) => {
    if (!date.isSame(currentMonthState, 'month')) {
      return true
    }

    if (minDate && date.isBefore(moment(minDate), 'day')) {
      return true
    }

    if (maxDate && date.isAfter(moment(maxDate), 'day')) {
      return true
    }

    // When enableAllDates is true, only disable weekends (Saturday = 6, Sunday = 0)
    if (enableAllDates) {
      const dayOfWeek = date.day()
      return dayOfWeek === 0 || dayOfWeek === 6 // Disable Sunday and Saturday
    }

    // Default behavior: check if date has available slots
    // But don't disable if unavailableDates includes this date (we want to show it as red but allow selection)
    const dateStr = date.format('YYYY-MM-DD')
    const dayMeta = availableDaysMap[dateStr]
    const hasSlots = Array.isArray(dayMeta?.slots) && dayMeta.slots.length > 0

    // If date is in unavailableDates, don't disable it (allow selection to change it)
    if (
      unavailableDates.includes(dateStr) ||
      restrictionDates.includes(dateStr)
    ) {
      return false
    }

    return !hasSlots
  }

  const handleDateClick = (date) => {
    if (isDisabled(date)) {
      return
    }

    const dateStr = date.format('YYYY-MM-DD')
    let newSelectedDates

    if (allowMultipleSelection) {
      if (selectedDates.includes(dateStr)) {
        newSelectedDates = selectedDates.filter((d) => d !== dateStr)
      } else {
        newSelectedDates = [...selectedDates, dateStr]
      }
    } else {
      newSelectedDates = [dateStr]
    }

    setSelectedDates(newSelectedDates)
    if (onDateSelect) {
      onDateSelect(
        allowMultipleSelection
          ? newSelectedDates
          : newSelectedDates.length > 0
          ? newSelectedDates[0]
          : null,
      )
    }
  }

  const handlePrevMonth = () => {
    const previousMonth = currentMonthState.clone().subtract(1, 'month')
    setCurrentMonthState(previousMonth)
    if (onMonthChange) {
      onMonthChange(previousMonth)
    }
  }

  const handleNextMonth = () => {
    const nextMonth = currentMonthState.clone().add(1, 'month')
    setCurrentMonthState(nextMonth)
    if (onMonthChange) {
      onMonthChange(nextMonth)
    }
  }

  const handleTodayClick = () => {
    const today = moment()
    const todayStr = today.format('YYYY-MM-DD')

    // Navigate to current month if not already viewing it
    if (!today.isSame(currentMonthState, 'month')) {
      const todayMonth = today.clone().startOf('month')
      setCurrentMonthState(todayMonth)
      if (onMonthChange) {
        onMonthChange(todayMonth)
      }
    }

    // Check if today is disabled
    if (isDisabled(today)) {
      return
    }

    let newSelectedDates

    if (allowMultipleSelection) {
      if (selectedDates.includes(todayStr)) {
        newSelectedDates = selectedDates.filter((d) => d !== todayStr)
      } else {
        newSelectedDates = [...selectedDates, todayStr]
      }
    } else {
      newSelectedDates = [todayStr]
    }

    setSelectedDates(newSelectedDates)
    if (onDateSelect) {
      onDateSelect(
        allowMultipleSelection
          ? newSelectedDates
          : newSelectedDates.length > 0
          ? newSelectedDates[0]
          : null,
      )
    }
  }

  const isCurrentMonth = (date) => {
    return date.isSame(currentMonthState, 'month')
  }

  const isToday = (date) => {
    return date.isSame(moment(), 'day')
  }

  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

  return (
    <div className='session-date- mt-1 p-3'>
      <div className='session-date-picker__header'>
        <span className='s6'>{currentMonthState.format('MMMM YYYY')}</span>
        <div className='session-date-picker__navigation'>
          <Button
            variant='abroad-blue'
            className='p-0 me-3 '
            onClick={handleTodayClick}
            disabled={isLoading}
            aria-label='Select today'>
            Today
          </Button>
          <Button
            variant='none'
            className='p-0 me-4 border-0'
            onClick={handlePrevMonth}
            disabled={isLoading}
            aria-label='Previous month'>
            <svg
              width='11'
              height='19'
              viewBox='0 0 11 19'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M9.49615 1.25L1.25 9.25003'
                stroke='black'
                stroke-width='2.5'
                stroke-linecap='round'
              />
              <path
                d='M9.49615 17.25L1.25 9.25'
                stroke='black'
                stroke-width='2.5'
                stroke-linecap='round'
              />
            </svg>
          </Button>
          <Button
            variant='none'
            className='p-0 ms-3 border-0'
            onClick={handleNextMonth}
            disabled={isLoading}
            aria-label='Next month'>
            <svg
              width='11'
              height='19'
              viewBox='0 0 11 19'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M1.24994 1.25L9.49609 9.25003'
                stroke='black'
                stroke-width='2.5'
                stroke-linecap='round'
              />
              <path
                d='M1.24994 17.25L9.49609 9.25'
                stroke='black'
                stroke-width='2.5'
                stroke-linecap='round'
              />
            </svg>
          </Button>
        </div>
      </div>
      <div className='session-date-picker__weekdays mt-3'>
        {weekDays.map((day) => (
          <div key={day} className='s8 text-center'>
            {day}
          </div>
        ))}
      </div>
      <div className='session-date-picker__days'>
        {isLoading ? (
          <div
            className='d-flex justify-content-center align-items-center'
            style={{
              gridColumn: '1 / -1',
              minHeight: '300px',
            }}>
            <Spinner
              animation='border'
              size='sm'
              className='font-saffron-700'
              role='status'
            />
          </div>
        ) : (
          <>
            {days.map((day, index) => {
              const dateStr = day.format('YYYY-MM-DD')
              const selected = isSelected(day)
              const disabled = isDisabled(day)
              const currentMonthDay = isCurrentMonth(day)
              const today = isToday(day)
              const isUnavailable = unavailableDates.includes(dateStr)
              const isRestriction = restrictionDates.includes(dateStr)
              const isHighlighted =
                !selected && highlightedDateSet.has(dateStr) && !disabled

              return (
                <button
                  key={index}
                  type='button'
                  className={classnames('session-date-picker__day', {
                    'session-date-picker__day--selected': selected,
                    'session-date-picker__day--highlighted': isHighlighted,
                    'session-date-picker__day--disabled': disabled,
                    'session-date-picker__day--other-month': !currentMonthDay,
                    'session-date-picker__day--today': today && !selected,
                    'session-date-picker__day--unavailable': isUnavailable,
                    'session-date-picker__day--restriction': isRestriction,
                  })}
                  onClick={() => handleDateClick(day)}
                  disabled={disabled}>
                  {day.format('D')}
                </button>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}

export default SessionDatePicker
